import { VIDEO_FPS } from '../constants'
import type { FrameSource, SyncDiagnostics } from './source'

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

/** Past this error a rate trim would take too long; jump instead. */
const HARD_SEEK_SECONDS = 0.12

/** Rate authority. Beyond a few percent the pitch-shifted picture is visible. */
const MAX_RATE_TRIM = 0.06

/** Writing playbackRate is not free; ignore changes smaller than this. */
const RATE_WRITE_EPSILON = 0.002

const KP = 1.6
const KI = 0.18
const INTEGRAL_LIMIT = 0.05

interface FrameReport {
  mediaTime: number
  displayTimeMs: number
}

/**
 * Fallback video path for browsers without WebCodecs.
 *
 * A `<video>` element runs on its own media clock, so it cannot be *told* which
 * frame to show — it can only be persuaded to keep pace. This closes a phase
 * lock around it, and differs from both previous builds in two ways that matter:
 *
 *  - **Phase is measured from `requestVideoFrameCallback`'s `mediaTime`**, the
 *    exact presentation timestamp of the frame that was actually shown, paired
 *    with the display time it was shown at. The old code polled
 *    `video.currentTime`, which is quantised and can lag reality by a couple of
 *    hundred milliseconds — so it was measuring its own error wrongly and then
 *    correcting against that.
 *  - **The correction is a PI controller with a small deadband**, rather than a
 *    proportional nudge rewritten on every animation frame. Desktop previously
 *    tolerated 90 ms before acting and rewrote `playbackRate` 60 times a second;
 *    mobile tolerated 450 ms and only checked every 4 seconds.
 *
 * This still cannot match the frame-exact path, and it does not pretend to —
 * `diagnostics.errorMs` reports the live measured error rather than a bound.
 */
export class ElementFrameSource implements FrameSource {
  readonly kind = 'element' as const

  private lastReport: FrameReport | null = null
  private errorSeconds = 0
  private integral = 0
  private appliedRate = 1
  private loopStart = 0
  private loopEnd = 0
  private loopEnabled = true
  private lastMusical = 0
  private seeking = false
  private playPending = false
  private disposed = false
  private rvfcHandle = 0

  constructor(
    private readonly video: HTMLVideoElement,
    /** Musical position that should be on screen at a `performance.now()` time. */
    private readonly musicalAtDisplayTime: (performanceMs: number) => number,
  ) {
    this.video.loop = false
    this.video.muted = true
    this.video.playsInline = true
    this.scheduleFrameCallback()
  }

  get width(): number {
    return this.video.videoWidth || 0
  }

  get height(): number {
    return this.video.videoHeight || 0
  }

  setLoop(start: number, end: number, enabled: boolean): void {
    this.loopStart = start
    this.loopEnd = end
    this.loopEnabled = enabled
  }

  acquire(): CanvasImageSource | null {
    return this.video.readyState >= 2 ? this.video : null
  }

  /**
   * The element always hands back the same object, so identity comes from the
   * timestamp of the frame it most recently presented. `requestVideoFrameCallback`
   * reports that exactly, which also means repaints track real presented frames
   * rather than the display refresh rate.
   */
  get presentedId(): number {
    return this.lastReport ? Math.round(this.lastReport.mediaTime * VIDEO_FPS) : -1
  }

  update(musical: number, tempo: number, playing: boolean): void {
    if (this.disposed) return

    if (!playing) {
      if (!this.video.paused) this.video.pause()
      this.writeRate(tempo)
      return
    }

    // A large backwards jump is the loop seam. The element cannot loop itself:
    // its own loop point is the media end at 24.05 s, not the musical loop at
    // 23.92 s, so wrapping is driven from here.
    if (musical < this.lastMusical - 1) {
      this.hardSeek(musical)
    }
    this.lastMusical = musical

    // Retry play if the element has stopped, but never stack requests: calling
    // play() on every animation frame queues 60 promises a second, and a
    // rejected autoplay attempt would otherwise be retried that fast forever.
    if (this.video.paused && !this.playPending) {
      this.playPending = true
      void this.video
        .play()
        .catch(() => undefined)
        .finally(() => {
          this.playPending = false
        })
    }

    const report = this.lastReport
    if (!report || this.seeking) return

    const target = this.musicalAtDisplayTime(report.displayTimeMs)
    let error = report.mediaTime - target
    if (this.loopEnabled) {
      // Fold the error across the seam so a wrap does not read as a huge lag.
      const span = Math.max(0.001, this.loopEnd - this.loopStart)
      if (error > span / 2) error -= span
      if (error < -span / 2) error += span
    }
    this.errorSeconds = error

    if (Math.abs(error) > HARD_SEEK_SECONDS) {
      this.hardSeek(target)
      return
    }

    this.integral = clamp(this.integral + error * 0.016, -INTEGRAL_LIMIT, INTEGRAL_LIMIT)
    const trim = clamp(-(KP * error + KI * this.integral), -MAX_RATE_TRIM, MAX_RATE_TRIM)
    this.writeRate(tempo * (1 + trim))
  }

  seek(musical: number): void {
    this.hardSeek(musical)
    this.lastMusical = musical
  }

  get diagnostics(): SyncDiagnostics {
    const ms = this.errorSeconds * 1000
    return {
      kind: 'Video element, phase-locked',
      errorMs: Math.abs(ms),
      ready: this.video.readyState >= 2,
      detail:
        this.lastReport === null
          ? 'Waiting for first presented frame.'
          : `Measured ${ms >= 0 ? 'picture ahead' : 'picture behind'} by ${Math.abs(ms).toFixed(1)} ms.`,
    }
  }

  dispose(): void {
    this.disposed = true
    const withCancel = this.video as HTMLVideoElement & {
      cancelVideoFrameCallback?: (handle: number) => void
    }
    if (this.rvfcHandle && withCancel.cancelVideoFrameCallback) {
      withCancel.cancelVideoFrameCallback(this.rvfcHandle)
    }
    this.video.pause()
  }

  private hardSeek(musical: number): void {
    this.integral = 0
    this.seeking = true
    try {
      this.video.currentTime = musical
    } catch {
      /* not seekable yet */
    }
    const done = () => {
      this.seeking = false
    }
    this.video.addEventListener('seeked', done, { once: true })
    // Some browsers drop `seeked` when a seek is superseded; do not hang.
    window.setTimeout(done, 400)
  }

  private writeRate(rate: number): void {
    if (Math.abs(rate - this.appliedRate) < RATE_WRITE_EPSILON) return
    this.appliedRate = rate
    try {
      this.video.playbackRate = rate
    } catch {
      /* rate out of range for this browser */
    }
  }

  private scheduleFrameCallback(): void {
    const withRvfc = this.video as HTMLVideoElement & {
      requestVideoFrameCallback?: (
        cb: (now: number, metadata: { mediaTime: number; expectedDisplayTime: number }) => void,
      ) => number
    }

    if (!withRvfc.requestVideoFrameCallback) {
      // Without rVFC there is no exact frame timestamp available. `currentTime`
      // is the only signal left; it is coarse, which is precisely why this is
      // the last resort.
      const poll = () => {
        if (this.disposed) return
        this.lastReport = {
          mediaTime: this.video.currentTime,
          displayTimeMs: performance.now(),
        }
        window.setTimeout(poll, 1000 / VIDEO_FPS)
      }
      poll()
      return
    }

    const onFrame = (_now: number, metadata: { mediaTime: number; expectedDisplayTime: number }) => {
      if (this.disposed) return
      this.lastReport = {
        mediaTime: metadata.mediaTime,
        displayTimeMs: metadata.expectedDisplayTime,
      }
      this.rvfcHandle = withRvfc.requestVideoFrameCallback!(onFrame)
    }
    this.rvfcHandle = withRvfc.requestVideoFrameCallback(onFrame)
  }
}
